import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Loader2, RefreshCw, Plug, Shield, AlertTriangle, CheckCircle, XCircle, Trash2, Download, Eye, Zap, Server } from 'lucide-react';
import { toast } from 'sonner';
import { getDLPPlatforms, getDLPConnections, createDLPConnection, deleteDLPConnection, testDLPConnection, syncDLPFindings, getDLPFindings, getDLPDashboard } from '@/services/dpdpa';

const PLATFORM_ICONS = { purview: '🟦', aws_macie: '🟧', gcp_dlp: '🟩', symantec: '🟪', generic: '⬜' };
const severityColor = (s) => ({ critical: 'bg-red-600 text-white', high: 'bg-orange-500 text-white', medium: 'bg-amber-500 text-white', low: 'bg-blue-500 text-white', info: 'bg-gray-400 text-white' })[s] || 'bg-gray-500 text-white';
const statusColor = (s) => ({ connected: 'text-emerald-600', configured: 'text-blue-600', syncing: 'text-amber-600', error: 'text-red-600' })[s] || 'text-gray-500';

export default function DLPConnectorPage() {
  const [platforms, setPlatforms] = useState([]);
  const [connections, setConnections] = useState([]);
  const [findings, setFindings] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showFindings, setShowFindings] = useState(null);
  const [syncing, setSyncing] = useState(null);
  const [testing, setTesting] = useState(null);
  const [form, setForm] = useState({ name: '', platform: '', credentials: {}, sync_frequency: 'manual' });
  const [activeTab, setActiveTab] = useState('connections');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [p, c, d] = await Promise.all([getDLPPlatforms(), getDLPConnections(), getDLPDashboard()]);
      setPlatforms(p.platforms || []);
      setConnections(c.connections || []);
      setDashboard(d);
    } catch { toast.error('Failed to load'); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.name || !form.platform) return toast.error('Name and platform required');
    try {
      await createDLPConnection(form);
      toast.success('Connection created');
      setShowCreate(false);
      setForm({ name: '', platform: '', credentials: {}, sync_frequency: 'manual' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleTest = async (connId) => {
    try {
      setTesting(connId);
      const r = await testDLPConnection(connId);
      toast[r.success ? 'success' : 'error'](r.message);
      load();
    } catch { toast.error('Test failed'); } finally { setTesting(null); }
  };

  const handleSync = async (connId) => {
    try {
      setSyncing(connId);
      const r = await syncDLPFindings(connId);
      toast.success(`Synced: ${r.findings_pulled} findings pulled, ${r.new_imported} new`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Sync failed'); } finally { setSyncing(null); }
  };

  const loadFindings = async (connId) => {
    try {
      const r = await getDLPFindings(connId);
      setFindings(r.findings || []);
      setShowFindings(connId);
    } catch { toast.error('Failed to load findings'); }
  };

  const selectedPlatform = platforms.find(p => p.id === form.platform);

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin opacity-50" /></div>;

  return (
    <div className="p-6 space-y-6" data-testid="dlp-connector-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">DLP Platform Connector</h1>
          <p className="text-sm opacity-60">Connect Microsoft Purview, AWS Macie, Google Cloud DLP and import findings</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowCreate(true)} data-testid="add-connection-btn"><Plus className="w-4 h-4 mr-2" /> Add Connection</Button>
          <Button onClick={load} variant="ghost" size="icon"><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="connections" data-testid="tab-connections"><Plug className="w-3.5 h-3.5 mr-1.5" /> Connections</TabsTrigger>
          <TabsTrigger value="dashboard" data-testid="tab-dlp-dashboard"><Shield className="w-3.5 h-3.5 mr-1.5" /> Findings Dashboard</TabsTrigger>
        </TabsList>

        <TabsContent value="connections" className="space-y-4 mt-4">
          {/* Platform cards */}
          {connections.length > 0 ? connections.map(conn => (
            <Card key={conn.id} data-testid={`conn-${conn.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{PLATFORM_ICONS[conn.platform] || '🔌'}</span>
                    <div>
                      <p className="font-medium">{conn.name}</p>
                      <p className="text-xs opacity-50">{platforms.find(p => p.id === conn.platform)?.name || conn.platform} &middot; {conn.sync_frequency}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-medium ${statusColor(conn.status)}`}>{conn.status}</span>
                    {conn.last_sync_at && <span className="text-[10px] opacity-40">Last: {new Date(conn.last_sync_at).toLocaleDateString()}</span>}
                    <Badge variant="secondary">{conn.total_findings_imported || 0} findings</Badge>
                    <Button size="sm" variant="outline" onClick={() => handleTest(conn.id)} disabled={testing === conn.id} data-testid={`test-${conn.id}`}>
                      {testing === conn.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleSync(conn.id)} disabled={syncing === conn.id} data-testid={`sync-${conn.id}`}>
                      {syncing === conn.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => loadFindings(conn.id)} data-testid={`view-${conn.id}`}><Eye className="w-3 h-3" /></Button>
                    <Button size="sm" variant="ghost" className="text-red-400" onClick={() => deleteDLPConnection(conn.id).then(load)} data-testid={`delete-${conn.id}`}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>
                {conn.error_message && <p className="text-xs text-red-500 mt-2">{conn.error_message}</p>}
              </CardContent>
            </Card>
          )) : (
            <div className="text-center py-12">
              <Server className="w-12 h-12 mx-auto mb-3 opacity-20" />
              <p className="text-sm opacity-50">No DLP connections configured. Add a connection to start importing findings.</p>
              <div className="flex justify-center gap-2 mt-4">
                {platforms.map(p => (
                  <Button key={p.id} variant="outline" size="sm" onClick={() => { setForm(f => ({ ...f, platform: p.id })); setShowCreate(true); }}>
                    {PLATFORM_ICONS[p.id]} {p.name}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="dashboard" className="space-y-4 mt-4">
          {dashboard && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold">{dashboard.total_findings}</p><p className="text-xs opacity-50">Total Findings</p></CardContent></Card>
                {Object.entries(dashboard.by_severity || {}).filter(([, v]) => v > 0).map(([sev, count]) => (
                  <Card key={sev}><CardContent className="p-3 text-center"><p className="text-2xl font-bold">{count}</p><p className="text-xs opacity-50 capitalize">{sev}</p></CardContent></Card>
                ))}
              </div>

              {dashboard.by_platform && Object.keys(dashboard.by_platform).length > 0 && (
                <Card data-testid="platform-breakdown">
                  <CardHeader className="pb-3"><CardTitle className="text-base">Findings by Platform</CardTitle></CardHeader>
                  <CardContent>
                    <div className="flex gap-4">
                      {Object.entries(dashboard.by_platform).map(([plat, count]) => (
                        <div key={plat} className="flex items-center gap-2">
                          <span>{PLATFORM_ICONS[plat] || '🔌'}</span>
                          <span className="text-sm">{plat}</span>
                          <Badge variant="secondary">{count}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {dashboard.recent_findings?.length > 0 && (
                <Card data-testid="recent-findings-card">
                  <CardHeader className="pb-3"><CardTitle className="text-base">Recent Findings</CardTitle></CardHeader>
                  <CardContent className="space-y-2">
                    {dashboard.recent_findings.map(f => (
                      <div key={f.id} className="flex items-center justify-between p-2 rounded border text-sm">
                        <div className="flex items-center gap-2">
                          <Badge className={severityColor(f.severity)}>{f.severity}</Badge>
                          <span>{f.title}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs opacity-40">{f.platform}</span>
                          <Badge variant="outline" className="text-[10px]">{f.resource_type}</Badge>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Findings Modal */}
      <Dialog open={!!showFindings} onOpenChange={() => setShowFindings(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Imported Findings</DialogTitle></DialogHeader>
          {findings.length > 0 ? (
            <div className="space-y-2">
              {findings.map(f => (
                <div key={f.id} className="p-3 rounded-lg border">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <Badge className={severityColor(f.severity)}>{f.severity}</Badge>
                      <span className="text-sm font-medium">{f.title}</span>
                    </div>
                    <span className="text-[10px] opacity-40">{f.finding_type}</span>
                  </div>
                  <p className="text-xs opacity-60">{f.description?.slice(0, 200)}</p>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <Badge variant="outline" className="text-[10px]">{f.resource_type}: {f.resource_name?.slice(0, 40)}</Badge>
                    {f.pii_types_detected?.map(t => <Badge key={t} variant="secondary" className="text-[10px]">{t}</Badge>)}
                    {f.dpdpa_domain_mapping?.map(d => <Badge key={d} className="text-[10px] bg-blue-500/10 text-blue-700">{d}</Badge>)}
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-center py-8 opacity-50">No findings imported yet. Sync the connection to pull findings.</p>}
        </DialogContent>
      </Dialog>

      {/* Create Connection Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add DLP Connection</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g., Production Purview" data-testid="conn-name-input" /></div>
            <div><Label>Platform</Label>
              <Select value={form.platform} onValueChange={v => setForm(p => ({ ...p, platform: v, credentials: {} }))}>
                <SelectTrigger data-testid="conn-platform-select"><SelectValue placeholder="Select platform" /></SelectTrigger>
                <SelectContent>
                  {platforms.map(p => <SelectItem key={p.id} value={p.id}>{PLATFORM_ICONS[p.id]} {p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {selectedPlatform?.description && <p className="text-xs opacity-50">{selectedPlatform.description}</p>}
            {selectedPlatform?.fields?.map(field => (
              <div key={field}>
                <Label className="capitalize">{field.replace(/_/g, ' ')}</Label>
                <Input type={field.includes('secret') || field.includes('key') || field.includes('password') ? 'password' : 'text'}
                  value={form.credentials[field] || ''} onChange={e => setForm(p => ({ ...p, credentials: { ...p.credentials, [field]: e.target.value } }))}
                  data-testid={`cred-${field}`} />
              </div>
            ))}
            <div><Label>Sync Frequency</Label>
              <Select value={form.sync_frequency} onValueChange={v => setForm(p => ({ ...p, sync_frequency: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button onClick={handleCreate} data-testid="create-conn-submit">Create Connection</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
